/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchButtonWrap,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import { Button } from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import { useTranslation } from 'react-i18next';
import { ComboBox, MultiComboBox } from 'components/selects/ComboBox';
import { Code } from 'models/common/CommonCode';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { ItemList, SetupExcpParam } from 'models/setup/excp/excp';
import {
  SubTitleLayout,
  SubTitleGroup,
  ControlBtnGroup,
  GlobalBtnGroup,
} from 'components/layouts/ContentLayout';
import CustomGrid from 'components/grids/CustomGrid';
import { useMessageBar } from 'hooks/useMessageBar';
import { useLoading } from 'components/process/Loading';
import useSessionStore from 'stores/useSessionStore';
import { SetupProject } from 'models/setup/SetupProject';
import { hasRole } from 'utils/SessionUtil';
import { SetupSchedule } from 'models/setup/SetupSchedule';
import { findExcpByElmPrjId } from 'apis/setup/SetupSchedule';
import _ from 'lodash';
import {
  getProjectBoxCnt,
  getSetupBoxList,
  saveBoxList,
  updateBoxProcedure,
} from 'apis/setup/excp/box';
import { CommonUtil } from 'utils/CommonUtil';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import { useCommonModal } from 'hooks/useCommonModal';
import { ControlFilldBG } from 'ui/theme/Color';
import { CrudCode } from 'models/common/Edit';
import WJCellTextarea from 'components/inputs/WJCellTextarea';
import { useLocation } from 'react-router-dom';
import GridSettingButtons from 'components/grids/GridSettingButtons';
import SetupBoxArrivalPopup from './SetupBoxArrivalPopup';
import { findDisplayProject } from 'apis/setup/SetupProject';

/**
 * @description 하역장관리 > Box 정보 조회
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 *  26.03.04    jihwan89      다른 페이지 MDI locationState의 영향을 받아 조회가 안되거나 변경되는 오류 수정
 */

interface BoxParam {
  elmPrjId?: string;
  excpId?: string;
}

const SetupBoxManagementPage = () => {
  const { t } = useTranslation();
  const { userId, deptCd, deptNm, elmPrjId, ensolYn } = useSessionStore();
  const { openMessageBar } = useMessageBar();
  const { openLoading } = useLoading();
  const { openCommonModal } = useCommonModal();
  const locationState = useLocation();
  const [locationParam, setLocationParam] = useState(locationState.state);

  const isEmailFormat = () => {
    if (!userId) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(userId);
  };

  const initSearchParam = {
    bildPlntCd: [],
    elmPrjId: locationParam?.elmPrjId || elmPrjId || '',
    excpId: ensolYn === 'Y' ? '' : deptCd,
    excpNm: ensolYn === 'Y' ? '' : deptNm,
    searchOwnedPrjYn: isEmailFormat() ? 'Y' : 'N',
  };

  const gridRef = useRef<any>();
  const boxGridUiRef = useRef<any>();
  const [flexRef, setFlexRef] = useState<any>();
  const [excpParam, setExcpParam] = useState<SetupExcpParam>({ ...initSearchParam });
  const [rowData, setRowData] = useState<ItemList[]>([]);
  const [errors, setErrors] = useState<any>({});
  const [prjMasterData, setPrjMasterData] = useState<SetupProject[]>([]); //프로젝트
  const [bildPlntCode, setBildPlntCode] = useState<Code[]>(); // 플랜트
  const [prjExcpData, setPrjExcpData] = useState<SetupSchedule[]>([]); //협력사
  const [projectCnt, setProjectCnt] = useState<SetupExcpParam>();
  const [isOpenCellTextarea, setOpenCellTextarea] = useState<boolean>(false);
  const [hitTest, setHitTest] = useState<any>();

  // 일괄 도착/Fab-In 처리 팝업
  const [isOpenArrivalPpopup, setIsOpenArrivalPpopup] = useState<boolean>(false);
  const [selectedRowData, setSelectedRowData] = useState<ItemList[]>([]);

  const getCommonCodes = async () => {
    const bildPlntOption: Code[] = await getCommonCodeNames('FA_SU_PLNT_CD'); // 플랜트코드
    setBildPlntCode(bildPlntOption);

    findDisplayProject().then((result: SetupProject[]) => {
      setPrjMasterData(result || []);

      if (!excpParam.elmPrjId && result?.length > 0) {
        setExcpParam((prev) => ({ ...prev, elmPrjId: result[0]?.elmPrjId || '' }));
      }
    });
  };

  const optionsPrjMaster = useMemo(() => {
    return (prjMasterData || [])
      .filter((o) =>
        (excpParam?.bildPlntCds || []).length > 0
          ? excpParam.bildPlntCds.includes(o.bildPlntCd)
          : true
      )
      .reduce(
        (acc, cur) => [
          ...acc,
          {
            cmnCd: cur.elmPrjId,
            cmnCdNm: cur.elmPrjNm,
          } as Code,
        ],
        [] as Code[]
      );
  }, [prjMasterData, excpParam?.bildPlntCds]);

  const optionsPrjExcp = useMemo(() => {
    return (prjExcpData || []).reduce(
      (acc, cur) => [
        ...acc,
        {
          cmnCd: cur.excpId,
          cmnCdNm: cur.excpNm,
        } as Code,
      ],
      [] as Code[]
    );
  }, [prjExcpData]);

  const getItemCnt = async () => {
    getProjectBoxCnt(excpParam).then((result: ItemList) => {
      if (result) {
        setProjectCnt(result);
      }
    });
  };

  const btnSearch = () => {
    setErrors({
      elmPrjId: _.isEmpty(excpParam.elmPrjId),
    });

    if (_.isEmpty(excpParam.elmPrjId)) {
      return;
    }
    openLoading(true);
    // 그리드 선택된 상태 제거
    getItemCnt();
    getSetupBoxList(excpParam)
      .then((result: ItemList[]) => {
        if (result !== null) {
          setRowData(result.map((o) => ({ ...o })));
        } else {
          openMessageBar({
            type: 'error',
            content: t(
              'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
              '요청 정보 조회 중 오류가 발생했습니다.'
            ),
          });
        }
      })
      .finally(() => openLoading(false));
  };

  const btnReload = () => {
    const isExcp = !hasRole('FA_DOVI') && isEmailFormat();
    setExcpParam((prev) => ({
      bildPlntCd: [],
      elmPrjId: '',
      excpId: isExcp ? prev.excpId : '',
      excpNm: isExcp ? prev.excpNm : '',
      searchOwnedPrjYn: isEmailFormat() ? 'Y' : 'N',
    }));
  };

  const handleOnChange = (name, value) => {
    setExcpParam((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({
      ...prev,
      [name]: false,
    }));
  };

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'crudKey',
        header: String(t('com.label.상태', '상태')),
        minWidth: 43,
        width: 43,
        isReadOnly: true,
        align: 'center',
        cellTemplate: GridStatusCellTemplate,
        visible: isEmailFormat(),
      },
      {
        binding: 'bmkYn',
        header: String(t('mp.grid.즐겨찾기', '즐겨찾기')),
        width: 90,
        align: 'center',
        cssClass: 'WijmoBookmark',
        isReadOnly: true,
        cellTemplate: (ctx) =>
          `<button class="${
            ctx.value === 'Y' ? 'detail' : ''
          }" id="bmkBtn" data-id="bmkBtn"></button>`,
      },
      {
        binding: 'pakBoxNo',
        header: String(t('com.label.Box No', 'Box No')),
        width: 160,
        align: 'center',
      },
      {
        binding: 'pakItemNm',
        header: String(t('com.label.품목 목록명', '품목 목록명')),
        width: 400,
        align: 'left',
      },
      {
        binding: 'bcdYn',
        header: String(t('com.label.바코드 관리', '바코드 관리')),
        width: 120,
        isReadOnly: true,
        align: 'center',
      },
      {
        binding: 'mcodes',
        header: String(t('com.label.M-Code List', 'M-Code List')),
        width: 180,
        isReadOnly: true,
        align: 'center',
      },
      {
        binding: 'arrivalYn',
        header: String(t('com.label.도착처리', '도착처리')),
        width: 100,
        isReadOnly: true,
        visible: isEmailFormat(),
        align: 'center',
        cssClass: 'WijmoBasicButton noTooltip',
        cellTemplate: (grid) => {
          const item = grid.item;

          if (item.bcdYn === 'Y') {
            return '';
          }

          if (['PACKING_DONE', 'SHIPPING_DONE'].includes(item.boxStatCd)) {
            return `
                <button class="approve" data-id="aliveBtn">
                  ${t('com.label.도착처리', '도착처리')}
                </button>`;
          } else {
            return '';
          }

          // if (grid.value !== 'Y') {
          //   return `
          //     <button class="approve" data-id="aliveBtn">
          //       ${t('com.label.도착처리', '도착처리')}
          //     </button>`;
          // } else {
          //   return '';
          // }
        },
      },
      {
        binding: 'fabinYn',
        header: String(t('com.label.Fab-In', 'Fab-In')),
        width: 100,
        isReadOnly: true,
        visible: isEmailFormat(),
        align: 'center',
        cssClass: 'WijmoBasicButton noTooltip',
        cellTemplate: (grid) => {
          const item = grid.item;

          if (item.bcdYn === 'Y') {
            return '';
          }

          if (['ARRIVAL_DONE', 'ARRIVAL_MANUAL'].includes(item.boxStatCd)) {
            return `
              <button class="approve" data-id="fabInBtn">
                ${t('com.label.Fab-In 처리', 'Fab-In 처리')}
              </button>`;
          } else {
            return '';
          }

          // if (grid.value !== 'Y' && item.arrivalYn === 'Y') {
          //   return `
          //     <button class="approve" data-id="fabInBtn">
          //       ${t('com.label.Fab-In 처리', 'Fab-In 처리')}
          //     </button>`;
          // } else {
          //   return '';
          // }
        },
      },
      {
        header: String(t('com.label.진행현황', '진행현황')),
        columns: [
          {
            binding: 'boxStatNm',
            header: String(t('com.label.현재상태', '현재상태')),
            width: 120,
            isReadOnly: true,
            align: 'center',
          },
          {
            binding: 'boxUnloDtm',
            header: String(t('com.label.도착처리', '도착처리')),
            width: 100,
            isReadOnly: true,
            align: 'center',
          },
          {
            binding: 'inptDtm',
            header: String(t('com.label.Fab-In', 'Fab-In')),
            width: 100,
            isReadOnly: true,
            align: 'center',
          },
          {
            binding: 'abnormalYn',
            header: String(t('com.label.이상처리', '이상처리')),
            width: 100,
            isReadOnly: true,
            align: 'center',
          },
        ],
      },
      {
        binding: 'ctnrSrno',
        header: String(t('com.label.CTNR No', 'CTNR No')),
        width: 200,
        align: 'center',
        dataType: 'Number',
      },
      {
        binding: 'rfidSn',
        header: String(t('com.label.RFID S/N', 'RFID S/N')),
        width: 200,
        align: 'left',
      },
      {
        binding: 'dataInsUserNm',
        header: String(t('com.label.작성자', '작성자')),
        width: 120,
        align: 'center',
      },
      {
        binding: 'dataInsDtm',
        header: String(t('com.label.작성일자', '작성일자')),
        width: 120,
        align: 'center',
      },
    ];
  }, [rowData]);

  const onInitialized = (grid) => {
    setFlexRef(grid);
    gridRef.current = grid;
    grid.hostElement.addEventListener('click', (e) => {
      e.stopPropagation();
      const ht = grid.hitTest(e);
      setHitTest(ht);
      const item = grid.rows[ht.row]?.dataItem;
      if (!item) return;

      if (item.rfidSn) {
        switch (ht.panel.columns[ht.col].binding) {
          case 'rfidSn': {
            setOpenCellTextarea(true);
            break;
          }
        }
      }
      if (item.pakItemNm) {
        switch (ht.panel.columns[ht.col].binding) {
          case 'pakItemNm': {
            setOpenCellTextarea(true);
            break;
          }
        }
      }
      if (!isEmailFormat()) return;
      const len = grid.rows.length;
      if (len == 0) return;

      if (ht.row < 0 || ht.col < 0) return;
      if (ht.panel.cellType != 1) return;
      if (e.target instanceof HTMLButtonElement) {
        switch (e.target.dataset.id) {
          case 'bmkBtn':
            item.bmkYn = item.bmkYn === null || item.bmkYn === 'N' ? 'Y' : null;
            item.crudKey = item.crudKey === null ? 'U' : null;
            gridRef.current.refresh();
            break;
          case 'aliveBtn':
          case 'fabInBtn':
            onSettingArrival(item);
            break;
        }
      }
    });
  };

  const btnExcelExport = () => {
    CommonUtil.exportWijmoExcelGrid(
      flexRef,
      t('setup.label.Box List 조회', 'Box List 조회'),
      t('setup.label.Box List', 'Box List')
    );
  };

  const btnSave = () => {
    openCommonModal({
      modalType: 'confirm',
      content: t('com.label.저장하시겠습니까?', '저장하시겠습니까?'),
      yesCallback: async () => {
        const response = await saveBoxList({
          boxList: rowData,
        });
        if (response == null || response?.successOrNot === 'N') {
          openMessageBar({
            type: 'error',
            content: t('com.label.처리 중 오류가 발생했습니다.', '처리 중 오류가 발생했습니다.'),
          });
          return;
        }
        openMessageBar({
          type: 'confirm',
          content: t('com.label.저장되었습니다.', '저장되었습니다.'),
        });
        btnSearch();
      },
    });
  };

  const btnSelectBmkYn = () => {
    const grid = gridRef.current;
    if (!grid) return;

    const rows = grid.rows;
    const len = rows.length;
    if (!rows || len === 0) {
      openMessageBar({
        type: 'error',
        content: t('com.label.데이터가 없습니다.', '데이터가 없습니다.'),
      });
      return;
    }

    const selectedRows: ItemList[] = [];
    for (const row of rows) {
      if (row.isSelected) {
        selectedRows.push(row.dataItem as ItemList);
      }
    }
    if (selectedRows.length === 0) {
      openMessageBar({
        type: 'error',
        content: t('setup.label.선택된 행이 없습니다.', '선택된 행이 없습니다.'),
      });
      return;
    }

    grid.beginUpdate();
    try {
      for (let i = 0; i < len; i++) {
        const row = rows[i];

        if (!row.isSelected) continue;

        const item = row.dataItem;
        if (!item) continue;

        item.bmkYn = 'Y';
        item.crudKey = CrudCode.UPDATE;
      }
    } finally {
      grid.endUpdate();
    }

    grid.collectionView?.refresh?.();
  };

  const closeArrivalPopup = () => {
    setIsOpenArrivalPpopup(false);
    btnSearch();
  };

  // 도착/Fab-In 처리 event
  const onSettingArrival = (item: ItemList) => {
    const boxList: ItemList[] = [];

    // 단일 처리
    boxList.push(item);

    openLoading(true);
    updateBoxProcedure({ boxList })
      .then((res) => {
        /**
         * success가 1건 이상인 경우 UI 업데이트 진행
         * 업데이트된 boxList를 list로 받아옴
         */
        if (res.data?.success && res.data?.list?.length) {
          /**
           * 도착/Fab-In 처리 진행 후 btnSearch() 함수로 데이터 조회시 FlexGrid는 새로운 itemSource로 인식해서 스크롤 초기화가 발생함
           * 스크롤 초기화를 하지 않고 데이터만 변경된 것 처럼 보이도록 하기 위해서
           * api에서 변경 후 데이터를 받아 grid.rows[].dataItem 값를 변경하고
           * grid.refresh() 함수로 변경내용은 grid에 적용함
           */
          const newItem = res.data.list[0];

          // 도착/Fab-In 처리 진행 후 변경되는 컬럼 update
          item.arrivalYn = newItem.arrivalYn; // 도착처리 : 버튼 표시 유무처리
          item.fabinYn = newItem.fabinYn; // Fab-In : 버튼 표시 유무처리
          item.boxUnloDtm = newItem.boxUnloDtm; // 진행현황>도착처리 : 도착날짜 표시
          item.inptDtm = newItem.inptDtm; // 진행현황>Fab-In : Fab-In날짜 표시
          item.boxStatCd = newItem.boxStatCd; // 진행현황>상태코드 : 현재상태랑 맞춤
          item.boxStatNm = newItem.boxStatNm; // 진행현황>현재상태 : 상태명 변경
          gridRef.current.refresh();

          openMessageBar({
            type: 'confirm',
            content: t(
              'com.msg.도착/Fab-In 처리가 완료되였습니다.',
              '도착/Fab-In 처리가 완료되였습니다.'
            ),
          });
        } else {
          openMessageBar({
            type: 'error',
            content: t(
              'com.msg.데이터 새로고침 후 다시 시도해주세요.',
              '데이터 새로고침 후 다시 시도해주세요.'
            ),
          });
        }
      })
      .catch((err) => {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.도착/Fab-In 처리가 실패하였습니다.',
            '도착/Fab-In 처리가 실패하였습니다.'
          ),
        });
      })
      .finally(() => openLoading(false));
  };

  // 일괄 도착/Fab-In 처리
  const onSettingAllArrival = () => {
    let boxList: ItemList[] = [];

    // 일괄 처리
    const grid = gridRef.current;
    if (!grid) return;

    boxList = grid.rows.filter((o) => o.isSelected).map((o) => o.dataItem);
    if (boxList.length === 0) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.선택된 항목이 없습니다.', '선택된 항목이 없습니다.'),
      });
      return;
    } else if (boxList.some((o) => o.bcdYn === 'Y')) {
      openMessageBar({
        type: 'error',
        content: t(
          'com.msg.바코드 관리가 "Y"인 항목이 선택되었습니다.',
          '바코드 관리가 "Y"인 항목이 선택되었습니다.'
        ),
      });
      return;
    } else if (boxList.some((o) => o.arrivalYn === 'Y' && o.fabinYn === 'Y')) {
      openMessageBar({
        type: 'error',
        content: t(
          'com.msg.이미 완료된 항목이 선택되었습니다.',
          '이미 완료된 항목이 선택되었습니다.'
        ),
      });
      return;
    }

    setSelectedRowData(boxList.map((o) => ({ ...o })));
    setIsOpenArrivalPpopup(true);
  };

  useEffect(() => {
    getCommonCodes();
  }, []);

  useEffect(() => {
    if (excpParam?.elmPrjId) {
      findExcpByElmPrjId(excpParam?.elmPrjId).then((result: SetupSchedule[]) => {
        setPrjExcpData(result);
      });
      btnSearch();
    } else {
      setPrjExcpData([]);
    }
  }, [excpParam?.elmPrjId]);

  useEffect(() => {
    if (location.pathname === '/setup/excp/box') {
      setLocationParam(locationState.state);
    }
  }, [locationState.key]);

  useEffect(() => {
    if (!_.isEmpty(locationParam)) {
      setExcpParam((prev) => ({
        ...prev,
        elmPrjId: locationParam?.elmPrjId,
        excpId: locationParam?.excpId,
      }));
    }
  }, [locationParam]);

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>{t('setup.label.플랜트', '플랜트')}</label>
                <MultiComboBox
                  id="bildPlntCd"
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={bildPlntCode || []}
                  defaultValue={excpParam.bildPlntCds || []}
                  onChange={(value) => {
                    handleOnChange('bildPlntCds', value);
                    if (
                      !(prjMasterData || []).filter(
                        (o) =>
                          excpParam?.elmPrjId === o.elmPrjId &&
                          ((value || []).length < 1 || (value || []).includes(o.bildPlntCd || ''))
                      ).length
                    ) {
                      handleOnChange('elmPrjId', '');
                      if (!isEmailFormat()) handleOnChange('excpId', '');
                    }
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>
                  <span className={'dot'}>{t('setup.label.프로젝트명', '프로젝트명')}</span>
                </label>
                <ComboBox
                  placeholder={String(t('com.label.선택', '선택'))}
                  options={optionsPrjMaster}
                  defaultValue={excpParam?.elmPrjId}
                  onChange={(value) => {
                    handleOnChange('elmPrjId', value);
                    if (!isEmailFormat()) handleOnChange('excpId', '');
                  }}
                  isError={errors?.elmPrjId}
                  msgError={String(
                    t('setup.label.프로젝트명을 선택해 주세요.', '프로젝트명을 선택해 주세요.')
                  )}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('setup.label.협력업체명', '협력업체명')}</label>
                {hasRole('FA_DOVI') || !isEmailFormat() ? (
                  <ComboBox
                    placeholder={String(t('com.label.전체', '전체'))}
                    options={optionsPrjExcp}
                    defaultValue={excpParam?.excpId}
                    onChange={(value) => {
                      handleOnChange('excpId', value);
                    }}
                  />
                ) : (
                  <div className="txt">
                    <span>{excpParam?.excpNm}</span>
                  </div>
                )}
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <Button css={IconButton.button} className="reload" disableRipple onClick={btnReload} />
            <Button css={IconButton.button} className="find" disableRipple onClick={btnSearch}>
              {t('com.button.조회', '조회')}
            </Button>
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.Box List', 'Box List')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{rowData?.length ?? 0}</span>
            {t('com.label.건', '건')}
          </span>
          {projectCnt && (
            <div className="info warning">
              {t('setup.label.컨테이너 적재율', '컨테이너 적재율')} : {projectCnt?.summary}
            </div>
          )}
        </SubTitleGroup>
        <ControlBtnGroup>
          {isEmailFormat() && (
            <>
              <Button
                css={IconButton.button}
                className="icoNone"
                onClick={onSettingAllArrival}
                disableRipple
              >
                {t('com.button.일괄 도착/Fab-In 처리', '일괄 도착/Fab-In 처리')}
              </Button>
              <Button
                css={IconButton.button}
                className="icoNone"
                onClick={btnSelectBmkYn}
                sx={{
                  '&&': {
                    color: '#fff',
                    background: ControlFilldBG.Primary,
                    '&:hover': {
                      background: ControlFilldBG.PrimaryHover,
                      color: '#fff',
                    },
                  },
                }}
              >
                {t('com.button.발행대상 일괄지정', '발행대상 일괄지정')}
              </Button>
            </>
          )}
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={btnExcelExport}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button css={IconButton.button} className="refresh" onClick={btnSearch} disableRipple>
            {t('com.button.새로고침', '새로고침')}
          </Button>
          <GridSettingButtons
            gridId={'box-grid-id'}
            layoutDefinition={layoutDefinition}
            changeShownColumns={(result) => {
              boxGridUiRef.current?.changeColumn?.(result);
            }}
          />
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        id={'box-grid-id'}
        ref={boxGridUiRef}
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        initialized={onInitialized}
        isSelector={isEmailFormat()}
        isReadOnly={true}
        height={530}
      />
      {isOpenCellTextarea && (
        <WJCellTextarea
          grid={flexRef}
          hitTest={hitTest}
          close={() => setOpenCellTextarea(false)}
          textAreaByteMax={4000}
          isReadOnly
        />
      )}
      {isEmailFormat() && (
        <GlobalBtnGroup>
          <Button css={IconButton.button} className="save" onClick={btnSave}>
            {t('com.button.저장', '저장')}
          </Button>
        </GlobalBtnGroup>
      )}
      {isOpenArrivalPpopup && (
        <SetupBoxArrivalPopup
          open={isOpenArrivalPpopup}
          close={closeArrivalPopup}
          boxList={selectedRowData}
        />
      )}
    </>
  );
};

export default SetupBoxManagementPage;
